import React, { useEffect } from "react";
import { AppState } from "../../store";
import { connect } from "react-redux";
import { IonItem, IonLabel, IonSelect, IonSelectOption, IonText } from "@ionic/react";
import CustomBox from "../../components/CustomBox";
import { loadPermitRegisterUsers } from "./thunks";
import { User } from "../../models/User";
import { service } from "./service";
import { Permit, getPermitGeofencing } from "../../models/Permit";
import { PermitAssignment } from "../../models/PermitAssignment";
import { Role, getPermitRoleSettings } from "../../models/Role";
import ChangePermitAssigneeModal from "../../components/ChangePermitAssigneeModal";
import { PermitAuthorisationSignature } from "../../models/PermitAuthorisationSignature";
import locationService, { GetPositionOptions, LocationErrorCode } from "../../LocationService";
import { updatePermitLastAccess } from "../PermitContainer/thunks";
import { ESRI_INTEGRATION_TYPE_MANAGED_BY_VISLOCK_ID } from "../../models/Client";

interface PermitAssignmentsContainerProps {
    permit: Permit;
    permitRegisterId?: string;
    permitTypeId?: string;
    permitGeofenceCheckResult: {
        isWithinGeofence: boolean;
        error: string | null;
        errorCode: LocationErrorCode | null;
    } | null;
    updatePermitLastAccess: typeof updatePermitLastAccess;
    loadPermitRegisterUsers: typeof loadPermitRegisterUsers;
    permitRegisterUsers: User[];
    clientRoles: Role[];
    currentUser: User | null;
    onPermitAssignmentSignatureSubmitted: (signature: PermitAuthorisationSignature, signatureDataUrl: string) => void;
    onPermitAssigneeUpdated: (assignment: PermitAssignment) => void;
    isReadOnly: boolean;
}

const PermitAssignmentsContainer: React.FC<PermitAssignmentsContainerProps> = ({
    permit,
    permitRegisterId,
    permitTypeId,
    permitGeofenceCheckResult,
    updatePermitLastAccess,
    loadPermitRegisterUsers,
    permitRegisterUsers,
    clientRoles,
    currentUser,
    onPermitAssignmentSignatureSubmitted,
    onPermitAssigneeUpdated,
    isReadOnly,
}) => {
    useEffect(() => {
        if (permitRegisterId && permitTypeId) {
            loadPermitRegisterUsers(permitRegisterId, permitTypeId);
        }
    }, [permitRegisterId, permitTypeId, loadPermitRegisterUsers]);

    const requestUpdatePermitLastAccess = (options?: GetPositionOptions) => {
        if (permit && currentUser) {
            updatePermitLastAccess(permit, currentUser.id, options);
        }
    };

    const handleRefreshButtonClicked = async () => {
        if (permitGeofenceCheckResult?.errorCode === LocationErrorCode.PERMISSION_DENIED) {
            await locationService.requestPermissions().catch(() => {});
        }
        requestUpdatePermitLastAccess({ maximumAge: 0 });
    };

    const handlePermitAssignmentChanged = (userId: string, assignment: PermitAssignment) => {
        const updatedAssignment = {
            id: assignment.id,
            roleId: assignment.roleId,
            userId: userId,
            assignedBy: currentUser?.id,
            dateAssigned: new Date(),
        } as PermitAssignment;

        onPermitAssigneeUpdated(updatedAssignment);
    };

    const handleChangePermitAssigneeModalSubmit = (
        signature: PermitAuthorisationSignature,
        signatureDataUrl: string
    ) => {
        const assignment = permit.permitAssignments?.find((a) => a.roleId === signature.roleId);

        if (assignment) {
            handlePermitAssignmentChanged(signature.userId, assignment);
        }

        onPermitAssignmentSignatureSubmitted(signature, signatureDataUrl);
    };

    const renderAvailableUsers = (user: User) => {
        return (
            <IonSelectOption key={user.id} value={user.id}>
                {user.firstName + " " + user.lastName}
            </IonSelectOption>
        );
    };

    if (!permitRegisterId || !permitTypeId) return null;

    const geofencing = getPermitGeofencing(permit);

    const abilityToSign =
        !(currentUser?.client.esriConfig?.integrationType.id === ESRI_INTEGRATION_TYPE_MANAGED_BY_VISLOCK_ID) ||
        !((geofencing?.status ?? "disabled") === "enforced") ||
        (permitGeofenceCheckResult?.isWithinGeofence ?? false);

    const renderAssignment = (assignment: PermitAssignment) => {
        const hasPermitAssigneeSigned = service.hasPermitAssigneeSigned(assignment, permit);
        const isChangePermitAssigneesAvailable = service.getChangePermitAssigneesAvailability(
            permit,
            currentUser,
            clientRoles
        );
        const assignee = permitRegisterUsers.find((u) => u.id === assignment.userId);
        const availableUsers = service.availableUsers(
            permitRegisterId!,
            permitTypeId!,
            permitRegisterUsers,
            assignment.roleId
        );
        const availableUsersToChange = availableUsers.filter((u) => u.id !== assignee?.id);

        const role = clientRoles.find((r) => r.id === assignment.roleId);
        const isPermitAssignmentReadOnly =
            isReadOnly ||
            hasPermitAssigneeSigned ||
            (role ? getPermitRoleSettings(role, permit.permitType?.id).mandatoryOnSubmission : undefined);

        return (
            <IonItem key={assignment.id}>
                <IonLabel class="ion-text-wrap">
                    <IonText color="medium">
                        <h3>{role?.name}</h3>
                    </IonText>
                    {isPermitAssignmentReadOnly ? (
                        <IonText>
                            <p style={{ paddingTop: 12 }}>
                                {assignee ? assignee.firstName + " " + assignee.lastName : "not selected"}
                            </p>
                        </IonText>
                    ) : (
                        <IonSelect
                            interface="alert"
                            value={assignment.userId}
                            placeholder="Select"
                            justify="start"
                            onIonChange={(e) => handlePermitAssignmentChanged(e.detail.value!, assignment)}
                        >
                            {availableUsers.map(renderAvailableUsers)}
                        </IonSelect>
                    )}
                </IonLabel>

                {isChangePermitAssigneesAvailable && (
                    <ChangePermitAssigneeModal
                        assignment={assignment}
                        role={role}
                        permitTypeId={permit.permitType?.id}
                        currentUser={currentUser}
                        availableUsers={availableUsersToChange}
                        onSubmit={handleChangePermitAssigneeModalSubmit}
                        abilityToSign={abilityToSign}
                        onRefreshButtonClicked={handleRefreshButtonClicked}
                        shouldShowGeofencingError={
                            (geofencing?.status ?? "disabled") !== "disabled" &&
                            !permitGeofenceCheckResult?.isWithinGeofence
                        }
                        geofencingError={permitGeofenceCheckResult?.error ?? null}
                    ></ChangePermitAssigneeModal>
                )}
            </IonItem>
        );
    };

    return (
        <CustomBox title="Permit Assignments">{permit.permitAssignments?.map((a) => renderAssignment(a))}</CustomBox>
    );
};

function mapStateToProps(state: AppState) {
    return {
        permitRegisterUsers: state.permitAssignments.permitRegisterUsers,
        permitGeofenceCheckResult: state.permitPage.permitGeofenceCheckResult,
        clientRoles: state.myPermits.clientRoles,
        permitRegisterId: state.permitPage.permit?.permitRegister?.id,
        permitTypeId: state.permitPage.permit?.permitType?.id,
        currentUser: state.user.currentUser,
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        loadPermitRegisterUsers: (permitRegisterId: string, permitTypeId: string) =>
            dispatch(loadPermitRegisterUsers(permitRegisterId, permitTypeId)),
        updatePermitLastAccess: (permit: Permit, userId: string, options?: GetPositionOptions) =>
            dispatch(updatePermitLastAccess(permit, userId, options)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PermitAssignmentsContainer);
